import type {
  BuyflowType,
  MarketInfo,
  ProductDeliveryChoice,
  ProductWidget,
} from '@/lib/TypesOpenApi';
import type {DeepReadonly} from '@/lib/typings/readonly';
import {
  type AnalyticsData,
  AnalyticsService,
  type AnalyticsWrapper,
  GOOGLE_FIBER_AFFILIATION,
  type Transaction,
  type TransactionItem,
} from '@gfiber/typesmith';
import * as buyflowUtil from './helper';
import {
  buildTransaction,
  buildTransactionData,
  buildTransactionItems,
} from './purchaseHelper';

/**
 * BYOR tag.
 */
export const BYOR_TAG = 'byor';

/**
 * GWifi tag.
 */
export const GWIFI_TAG = 'google wifi';

/**
 * GALabel object is combined to form the label
 */
export interface GALabel {
  subType: string;
  serviceName?: string;
}

/**
 * Service type definition.
 */
export type ServiceType =
  | 'internet speed'
  | 'tv package'
  | 'phone service'
  | 'additional services'
  | 'streaming'
  | 'ott box'
  | 'youtube tv'
  | 'yttv details'
  | 'static ip';

/**
 * Section names for GA tracking
 */
export type AccordionName =
  | 'additional tv packages'
  | 'phone services'
  | 'streaming services'
  | 'static ip'
  | 'live tv services';

const RESIDENTIAL_MODULAR_NAV = 'modularsignup nav';
const SMB_MODULAR_NAV = 'smb - modularsignup_nav';
const RESIDENTIAL_MODULAR = 'modularsignup';
const ADMIN_CX_MODULAR = 'admin cx';
const ADMIN_CX_MODULAR_NAV = 'admin cx - modularsignup_nav';
const SMB_MODULAR = 'smb';
const CHECK_ELIGIBLE = 'checkEligible';

/** Admin buyflow analytics tracking path. */
export const ADMIN_BUYFLOW_TRACKING_URL = '/signup/modular/admin';

/**
 * Buyflow `Next` button tag.
 */
export const NEXT = 'next';

/**
 * Buyflow BYOR tag.
 */
export const RES_MODULAR_BYOR = 'modularsignup byor';

/**
 * Buyflow root path step name.
 */
export const START_BUYFLOW_STEP = 'start_buyflow';

/**
 * Injectable Google Analytics service for Modular Signup App.
 *
 * 1. getAppName has to return correct app name and it requires information about buyflowType
 * 2. getLabelPrefix has to return part of ccategory and also requires information about buyflowType
 *
 * As a result SignupAnalyticsService must send event data only when buyflowType is settled.
 * For that wrappers are used
 */
export class SignupAnalyticsService extends AnalyticsService {
  private eventQueue: Array<
    [
      category: Function | string,
      action: string,
      label?: string,
      additionalData?: AnalyticsData,
    ]
  > = [];

  private isAddressIsInstallReady = false;
  private stepName = '';

  constructor(
    wrapper: AnalyticsWrapper,
    private buyflowType: BuyflowType | undefined,
  ) {
    super(wrapper);
  }

  override getAppName() {
    // unknown type at the start of buyflow
    if (this.stepName === START_BUYFLOW_STEP) {
      return 'gfiber-signup-unknown';
    }
    if (buyflowUtil.isAdminCx(this.buyflowType)) {
      return 'gfiber-signup-admin cx';
    }
    if (buyflowUtil.isSmb(this.buyflowType)) {
      return 'gfiber-signup-smb';
    }
    return 'gfiber-signup-residential';
  }

  private getNavLabel(): string {
    if (buyflowUtil.isAdminCx(this.buyflowType)) {
      return ADMIN_CX_MODULAR_NAV;
    }

    if (buyflowUtil.isSmb(this.buyflowType)) {
      return SMB_MODULAR_NAV;
    }
    return RESIDENTIAL_MODULAR_NAV;
  }

  getLabelPrefix(): string {
    if (buyflowUtil.isAdminCx(this.buyflowType)) {
      return `${ADMIN_CX_MODULAR} -`;
    }
    if (buyflowUtil.isSmb(this.buyflowType)) {
      return `${SMB_MODULAR} -`;
    }
    return RESIDENTIAL_MODULAR;
  }

  private getPhonepageCategoryName() {
    return `${this.getLabelPrefix()} phone`;
  }

  // Converts GALabel object into label string
  private static createLabel(gaLabel?: GALabel): string {
    if (!gaLabel) {
      return '';
    }
    if (!gaLabel.serviceName) {
      return gaLabel.subType;
    }
    return `${gaLabel.subType}:${gaLabel.serviceName}`;
  }

  setBuyflowType(buyflowType: BuyflowType) {
    this.buyflowType = buyflowType;
    this.runQueuedEvents();
  }

  setMarketInfo(marketInfo?: MarketInfo) {
    if (marketInfo?.marketCode) {
      this.setField('marketCode', marketInfo.marketCode);
    }
    if (marketInfo?.metroCode) {
      this.setField('metroCode', marketInfo.metroCode);
    }
  }

  setAddressType(primaryAddressType: string, secondaryAddressType: string) {
    const primPrefixLen = 'PRIMARY_ADDRESS_TYPE_'.length;
    const secondPrefixLen = 'SECONDARY_ADDRESS_TYPE_'.length;
    if (primaryAddressType.length > primPrefixLen) {
      let addressType = primaryAddressType.substring(primPrefixLen);
      if (secondaryAddressType.length > secondPrefixLen) {
        addressType = `${addressType}-${secondaryAddressType.substring(secondPrefixLen)}`;
      }
      this.setField('addressType', addressType);
    }
  }
  setDaysEligible(eligibleSinceSecond: number) {
    if (eligibleSinceSecond) {
      this.setField(
        'daysEligible',
        Math.floor(
          (new Date().getTime() / 1000 - eligibleSinceSecond) / 3600 / 24,
        ),
      );
      // Sets inventory type depending on the year address became eligible.
      this.setField(
        'inventoryType',
        this.getInventoryType(eligibleSinceSecond),
      );
    }
  }

  setInternetPlans(planNames: string[]) {
    if (planNames.length > 0) {
      this.setField('plansOffered', planNames);
    }
  }

  setStepName(stepName: string) {
    // TODO: remove 'buyflowStepName' later.
    this.stepName = stepName;
    this.setField('buyflowStepName', stepName);
    this.setField('stepName', stepName);
  }

  getBuyflowType() {
    return this.buyflowType;
  }

  trackCallButton() {
    this.sendEventWrapper(() => this.getNavLabel(), 'call', '');
  }

  trackOpenLiveChat() {
    this.sendEventWrapper(() => this.getNavLabel(), 'chat', '');
  }

  trackTransaction(
    transaction: Transaction,
    items: TransactionItem[],
    pageName?: string,
    routerType?: string,
    additionalData?: AnalyticsData,
  ): Promise<void> {
    const data = buildTransactionData(
      transaction,
      items,
      pageName,
      routerType,
      additionalData,
    );
    return this.push(data);
  }

  trackRevenue(transactionItems: TransactionItem[], routerType: string) {
    let monthlyRevenue = 0;
    let upfrontRevenue = 0;
    const additionalData: AnalyticsData = {};
    for (const item of transactionItems) {
      monthlyRevenue += item.price * item.quantity;
      upfrontRevenue += item.upfrontPrice * item.quantity;

      if (item.category === 'plan') {
        additionalData.planName = item.name;
      }
    }
    const transaction = buildTransaction(
      GOOGLE_FIBER_AFFILIATION,
      monthlyRevenue,
      upfrontRevenue,
    );

    return this.trackTransaction(
      transaction,
      transactionItems,
      '/signup/final_step', // TODO dynamic page name, if it matters.
      routerType,
      additionalData,
    );
  }

  trackAnalyticsRevenue = (
    products: DeepReadonly<ProductWidget[]>,
    routerType: string,
  ): Promise<void> => {
    const transactionItems = buildTransactionItems(products);
    return this.trackRevenue(transactionItems, routerType);
  };

  trackPlaceOrder(
    products: DeepReadonly<ProductWidget[]>,
    deliveryChoice?: ProductDeliveryChoice,
  ): Promise<void> {
    const routerType = deliveryChoice === 'BYOR' ? 'byor' : 'fiber';
    return this.trackAnalyticsRevenue(products, routerType);
  }

  trackLiveChatAvailability(available = true): Promise<void> {
    return this.sendEvent(
      this.getNavLabel(),
      `chat ${available ? 'available' : 'unavailable'}`,
      '',
    );
  }

  trackCartButton(): Promise<void> {
    return this.sendEvent(this.getNavLabel(), 'cart', '');
  }

  trackBackButton(stepName = '', label = '') {
    this.trackStepActionButton(stepName, 'back button', label);
  }

  trackNextButton(stepName = '', label = '', additionalData?: AnalyticsData) {
    this.trackStepActionButton(
      stepName,
      stepName === 'preconfig_step' ? 'get started' : 'next',
      label,
      additionalData,
    );
  }

  trackStepActionButton(
    stepName: string,
    stepAction: string,
    stepLabel?: string,
    additionalData?: AnalyticsData,
  ) {
    this.sendEventWrapper(
      () => `${this.getLabelPrefix()} ${stepName}`,
      stepAction.toLocaleLowerCase(),
      stepLabel || '',
      additionalData,
    );
  }

  trackOptionCta(stepName: string, cta: string): Promise<void> {
    return this.sendEvent(`${this.getLabelPrefix()} ${stepName}`, `${cta}`, '');
  }

  private getStreamingCategoryName() {
    return `${this.getLabelPrefix()} streaming service`;
  }

  trackStreamingCardSelection(
    prevValue: string | undefined,
    newValue: string | undefined,
  ) {
    this.trackCardSelection(
      'streaming service',
      this.getStreamingCardSelectionValue(prevValue),
      this.getStreamingCardSelectionValue(newValue),
    );
  }

  getStreamingCardSelectionValue(value: string | undefined): string {
    if (!value) {
      return '';
    }
    if (value === 'noStreaming') {
      return 'no_streaming';
    }
    return 'streaming';
  }

  trackStreamingServicesSelection(): void {
    this.sendEventWrapper(
      () => this.getStreamingCategoryName(),
      'view and select services',
      '',
    );
  }

  trackStreamingServiceProductSelection(
    serviceName: string,
    added: boolean,
  ): void {
    const action = added ? 'select' : 'deselect';
    this.sendEventWrapper(
      () => this.getStreamingCategoryName(),
      action,
      `live_tv:${serviceName}`,
    );
  }

  trackAddServices(names: string[]): void {
    this.sendEventWrapper(
      () => this.getStreamingCategoryName(),
      'add services',
      names.map(n => `live_tv: ${n}`).join(', '),
    );
  }

  trackAdditionalWiFiPoints(pointsNumber: number): Promise<void> {
    return this.sendEvent(
      'modularsignup google wifi',
      'add google wifi point',
      pointsNumber.toString(),
    );
  }

  trackCheckEligible(zipCode: string): Promise<void> {
    const checkedSession = window.sessionStorage.getItem(CHECK_ELIGIBLE);
    const checkedLocalStorage = window.localStorage.getItem(CHECK_ELIGIBLE);
    if (!zipCode) {
      return Promise.resolve();
    }
    const simplifiedZip = zipCode.slice(0, 5);
    if (!checkedSession) {
      this.sendEventFor('checkEligibleSession', {zipCode: simplifiedZip});
      window.sessionStorage.setItem(CHECK_ELIGIBLE, '1');
    }
    if (!checkedLocalStorage) {
      this.sendEventFor('checkEligible', {zipCode: simplifiedZip});
      window.localStorage.setItem(CHECK_ELIGIBLE, '1');
    }
    return Promise.resolve();
  }

  // Deprecated
  trackAddFiberPhone(): Promise<void> {
    return this.sendEvent(
      'modularsignup phone_service_step',
      'add google fiber phone',
      '',
    );
  }

  trackAddPhoneFromDrawer() {
    this.sendEventWrapper(
      () => this.getPhonepageCategoryName(),
      'add google fiber phone',
      '',
    );
  }

  trackLearnMoreAddPhone() {
    this.sendEventWrapper(
      () => this.getPhonepageCategoryName(),
      'Learn more',
      '',
    );
  }

  trackPhoneCardSelection(
    prevValue: string | undefined,
    newValue: string | undefined,
  ) {
    this.trackCardSelection('phone', prevValue, newValue);
  }

  trackCalculateWiFi(restart = false): Promise<void> {
    return this.sendEvent(
      'modularsignup google wifi',
      restart ? 'restart calculation' : 'calculate',
      '',
    );
  }

  /**
   * Tracks when any card is selected on the options step page.
   */
  trackCardSelection(
    stepName: string,
    oldSelection: string | undefined,
    newSelection: string | undefined,
  ) {
    this.sendEventWrapper(
      () => `${this.getLabelPrefix()} ${stepName}`,
      'card selection',
      `${oldSelection || ''}:${newSelection || ''}`,
      {
        previousOptionSelection: oldSelection,
        currentOptionSelection: newSelection,
      },
    );
  }

  /**
   * Tracks when any option card is selected on the Preconfig page.
   */
  trackPreconfigCardSelection(optionName: string, newSelection: string) {
    const category = `preconfig - ${optionName}`;

    this.sendEventWrapper(category, newSelection);
  }

  trackInternetPlanButtonSelection(action: string, label: string | undefined) {
    this.sendEventWrapper(
      () => `${this.getLabelPrefix()} pick_plan`,
      action,
      label,
    );
  }
  trackInternetPlanCardSelection(oldSelection: string, newSelection: string) {
    this.trackCardSelection('pick_plan', oldSelection, newSelection);
  }

  trackBasicInternetPlanPopupOpened(label: string) {
    this.trackInternetPlanButtonSelection('basic internet', label);
  }

  trackBasicInternetPlanDrawerPrimaryButton() {
    this.trackInternetPlanButtonSelection(
      'confirm',
      'Basic Internet pop-up modal',
    );
  }

  trackInternetBackupBatteryButton(action: string) {
    this.trackInternetPlanButtonSelection(action, 'internet battery backup');
  }

  trackInternetBackupBatteryCheckbox(isChecked: boolean) {
    const selectedLabel = 'battey';
    const nonSelectedLabel = 'no_battery';
    const label = isChecked
      ? `${nonSelectedLabel}:${selectedLabel}`
      : `${selectedLabel}:${nonSelectedLabel}`;
    this.trackInternetPlanButtonSelection('select checkbox', label);
  }

  trackInternetPlanActivationPopup(label: string) {
    this.trackInternetPlanButtonSelection('modal shown', label);
  }

  trackSikShippingButtonSelection(
    action: string,
    label: string | undefined,
    isOnlySik: boolean,
  ) {
    const eventCategoryPostfix = isOnlySik ? 'sik_shipping' : 'store_pickup';
    this.sendEventWrapper(
      () => `${this.getLabelPrefix()} ${eventCategoryPostfix}`,
      action,
      label,
    );
  }

  trackSikShippingCardSelection(oldSelection: string, newSelection: string) {
    this.trackCardSelection('store_pickup', oldSelection, newSelection);
  }

  /**
   * Wrapper to track open assistance dialogs.
   */
  trackAssistance(serviceType: ServiceType, gaLabel?: GALabel): Promise<void> {
    return this.sendEvent(
      `singlesignup ${serviceType}`,
      'open assistance',
      SignupAnalyticsService.createLabel(gaLabel),
    );
  }

  trackValidPhoneNumber(): Promise<void> {
    return this.sendEvent(
      `${this.getLabelPrefix()} contact info`,
      'valid phone entered',
      '',
    );
  }

  /**
   * Tracks when any of the sections is expanded
   */
  trackExpand(
    category: ServiceType,
    expanded: boolean,
    accordionName: AccordionName,
  ): Promise<void> {
    const action = expanded ? 'open' : 'close';
    return this.sendEvent(
      `singlesignup ${category}`,
      `${action} options`,
      accordionName,
    );
  }

  /**
   * Tracks when any checkbox in the additional options is selected or
   * deselected, as well as the TV slider
   */
  trackOptionsSelection(
    serviceType: ServiceType,
    value: boolean,
    gaLabel?: GALabel,
  ): Promise<void> {
    const selectAction = value ? 'select' : 'deselect';

    return this.sendEvent(
      `singlesignup ${serviceType}`,
      selectAction,
      SignupAnalyticsService.createLabel(gaLabel),
    );
  }

  trackCreateAccountButtonClick(): Promise<void> {
    return this.sendEvent(
      `${this.getLabelPrefix()} signin speedbump`,
      'create a google account',
      '',
    );
  }

  trackSignInButtonClick(): Promise<void> {
    return this.sendEvent(
      `${this.getLabelPrefix()} signin speedbump`,
      'sign in',
      '',
    );
  }

  trackContactInfoNext(): Promise<void> {
    return this.sendEvent(
      `${this.getLabelPrefix()} signin speedbump`,
      NEXT,
      '',
    );
  }

  trackScheduleTimeSelected(days: number): Promise<void> {
    return this.trackScheduleEvent('select appointment', {
      installDaysOut: days,
    });
  }

  trackScheduleShowMoreDates(): Promise<void> {
    return this.trackScheduleEvent('show more dates');
  }

  trackScheduleFirstAppointment(seconds: number): Promise<void> {
    return this.trackScheduleEvent('first appointment', {
      'start time': seconds,
    });
  }

  setFirstInstallDateNonIru(date: string): void {
    if (date && !this.isAddressIsInstallReady) {
      this.setField('firstInstallDateNonIru', date);
    }
  }

  setAvailableAppointmentsTimestamps(timestamps: number[]) {
    const timestampsString = timestamps.toString();
    this.setField(
      'availableAppointmentsTimestamps',
      timestampsString.substring(0, 99),
    );
  }

  trackScheduleLoaded(): Promise<void> {
    return this.trackScheduleEvent('loaded appointments');
  }

  trackScheduleEvent(
    label: string,
    additionalData?: AnalyticsData,
  ): Promise<void> {
    return this.sendEvent(
      `${this.getLabelPrefix()} installation`,
      'schedule',
      label,
      additionalData,
    );
  }

  trackContactInfoFieldFocus(label: string): Promise<void> {
    return this.trackContactInfo('focus', label);
  }

  trackContctInfoChangeAccount(): Promise<void> {
    return this.trackContactInfo('change account', '');
  }

  trackContactInfoDasherAlert(action: string): Promise<void> {
    return this.trackContactInfo(
      action,
      'signup-verification-soft-block account signin',
    );
  }

  private trackContactInfo(action: string, label: string): Promise<void> {
    return this.sendEvent(
      `${this.getLabelPrefix()} contact info`,
      action,
      label || '',
    );
  }

  trackWifiSetupEvent(): Promise<void> {
    return this.sendEvent(
      `${this.getLabelPrefix()} instant_activation`,
      "i'll do this later",
      '',
    );
  }

  getInventoryType(eligibleSinceSecond: number): string {
    const milliSecondDiff = Date.now() - eligibleSinceSecond * 1000;
    const diffDate = new Date(milliSecondDiff);
    const yearDiff = Math.abs(diffDate.getUTCFullYear() - 1970);
    return yearDiff < 1 ? 'NI' : 'AO';
  }

  // Tracking of isByorSelected for all eligible buyflow steps.
  trackByorNext(isByorSelected: boolean): Promise<void> {
    return this.sendEvent(
      RES_MODULAR_BYOR,
      NEXT,
      `isByorSelected: ${isByorSelected}`,
    );
  }

  trackByorSelection(routerName: string, added: boolean): Promise<void> {
    const action = added ? 'select' : 'deselect';
    return this.sendEvent(
      RES_MODULAR_BYOR,
      action,
      `router name: ${routerName}`,
    );
  }

  trackExperementEvents(
    experiments: {
      experimenteeUserId?: string;
      experimentKey?: string;
      flagKey?: string;
      variationKey?: string;
    }[],
  ): void {
    for (let index = 0; index < experiments.length; index++) {
      const experiment = experiments[index];
      this.sendHit('optimizely-decision', {
        'optimizely-experimentKey': experiment.experimentKey,
        'optimizely-flagKey': experiment.flagKey,
        'optimizely-variationKey': experiment.variationKey,
      });
    }
  }

  /**
   * Tracks time spend on the page.
   */
  trackTimeSpendOnPage(
    page: string,
    startTime: number,
    endTime: number,
  ): Promise<void> {
    const milliseconds = endTime - startTime;
    return this.sendTiming(page, 'time spent', milliseconds);
  }

  sendEventWrapper(
    category: Function | string,
    action: string,
    label?: string,
    additionalData?: AnalyticsData,
  ) {
    if (!this.buyflowType) {
      // Add to queue
      this.eventQueue.push([category, action, label, additionalData]);

      return;
    }

    const categoryResult: string =
      typeof category === 'function' ? category() : category;

    this.sendEvent(categoryResult, action, label, additionalData);
  }

  runQueuedEvents() {
    this.eventQueue.forEach(eData => {
      this.sendEventWrapper(...eData);
    });
    this.eventQueue = [];
  }

  setAddressEquipmentState(addressState: string | undefined) {
    this.isAddressIsInstallReady =
      addressState === 'ADDRESS_NIU_INSTALLED' ||
      addressState === 'ADDRESS_ONT_INSTALLED';
  }
}
